import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, Transition } from '@headlessui/react';
import { useUser } from '../context';
import type { SchoolPayload, StudentPayload } from './checkInTypes';

type AttendanceType = 'checkin' | 'checkout';

type AttendanceHistoryRecord = {
  id: string;
  day: string;
  attendance: AttendanceType;
  by: string;
  data: unknown;
  at: string;
  student: StudentPayload | null;
};

type AttendanceHistoryResponse = {
  days: string[];
  students: StudentPayload[];
  attendance: AttendanceHistoryRecord[];
};

type AttendanceHistoryPanelProps = {
  school: SchoolPayload;
};

type AttendanceDayRow = {
  key: string;
  student: StudentPayload | null;
  checkin: AttendanceHistoryRecord | null;
  checkout: AttendanceHistoryRecord | null;
};

type PendingAction = {
  student: StudentPayload;
  attendance: AttendanceType;
};

const formatDay = (day: string) =>
  new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${day}T00:00:00.000Z`));

const studentKey = (student: StudentPayload | null) => student?.id ?? 'removed';

const studentDisplayName = (student: StudentPayload) =>
  `${student.firstName} ${student.lastName}`.trim();

const staffDisplayName = (user: {
  firstName?: string;
  lastName?: string;
  email: string;
}) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email;
};

const AttendanceRecordsPrintSheet = ({
  schoolName,
  dayLabel,
  filterSummary,
  rows,
  showCheckInColumn,
  showCheckOutColumn,
  formatEvent,
}: {
  schoolName: string;
  dayLabel: string;
  filterSummary: string;
  rows: AttendanceDayRow[];
  showCheckInColumn: boolean;
  showCheckOutColumn: boolean;
  formatEvent: (record: AttendanceHistoryRecord | null) => string;
}) => {
  const sheet = (
    <div id="attendance-records-print-root" aria-hidden>
      <h1 className="attendance-print-title">{schoolName}</h1>
      <p className="attendance-print-subtitle">Attendance records · {dayLabel}</p>
      {filterSummary ? (
        <p className="attendance-print-filters">{filterSummary}</p>
      ) : null}
      <table className="attendance-print-table">
        <thead>
          <tr>
            <th>Student</th>
            <th>Grade</th>
            <th>Room</th>
            {showCheckInColumn ? <th>Check-in</th> : null}
            {showCheckOutColumn ? <th>Check-out</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>
                {row.student
                  ? `${row.student.firstName} ${row.student.lastName}`
                  : 'Student removed'}
              </td>
              <td>{row.student?.grade || '—'}</td>
              <td>{row.student?.room || '—'}</td>
              {showCheckInColumn ? <td>{formatEvent(row.checkin)}</td> : null}
              {showCheckOutColumn ? <td>{formatEvent(row.checkout)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(sheet, document.body);
};

const AttendanceHistoryPanel = ({ school }: AttendanceHistoryPanelProps) => {
  const { user } = useUser();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [records, setRecords] = useState<AttendanceHistoryRecord[]>([]);
  const [students, setStudents] = useState<StudentPayload[]>([]);
  const [days, setDays] = useState<string[]>([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedRoom, setSelectedRoom] = useState('');
  const [showCheckedInOnly, setShowCheckedInOnly] = useState(false);
  const [showCheckedOutOnly, setShowCheckedOutOnly] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [printing, setPrinting] = useState(false);

  const load = useCallback(async (options?: { quiet?: boolean }) => {
    if (!options?.quiet) {
      setStatus('loading');
    }
    setError(null);

    try {
      const response = await fetch('/api/attendance/history', {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error(`Failed to load attendance (${response.status})`);
      }

      const data = (await response.json()) as AttendanceHistoryResponse;
      setRecords(data.attendance);
      setStudents(data.students);
      setDays(data.days);
      setSelectedDay((current) =>
        current && data.days.includes(current) ? current : data.days[0] ?? '',
      );
      setStatus('ready');
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Failed to load attendance');
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!printing) return;

    const previousTitle = document.title;
    const dayLabel = selectedDay ? formatDay(selectedDay) : 'Attendance';
    document.title = `${school.name} — attendance ${dayLabel}`;
    document.body.classList.add('printing-attendance-records');

    let cleared = false;
    const clearPrint = () => {
      if (cleared) return;
      cleared = true;
      document.body.classList.remove('printing-attendance-records');
      document.title = previousTitle;
      setPrinting(false);
    };

    const onAfterPrint = () => clearPrint();
    window.addEventListener('afterprint', onAfterPrint);

    const printTimer = window.setTimeout(() => {
      window.print();
    }, 150);

    const fallbackTimer = window.setTimeout(clearPrint, 60_000);

    return () => {
      window.clearTimeout(printTimer);
      window.clearTimeout(fallbackTimer);
      window.removeEventListener('afterprint', onAfterPrint);
      document.body.classList.remove('printing-attendance-records');
      document.title = previousTitle;
    };
  }, [printing, school.name, selectedDay]);

  const grades = useMemo(
    () =>
      Array.from(
        new Set([
          ...school.grades,
          ...students.map((student) => student.grade).filter(Boolean),
        ]),
      ).sort((left, right) => left.localeCompare(right, undefined, { numeric: true })),
    [students, school.grades],
  );

  const rooms = useMemo(
    () =>
      Array.from(
        new Set([...school.rooms, ...students.map((student) => student.room).filter(Boolean)]),
      ).sort((left, right) => left.localeCompare(right, undefined, { numeric: true })),
    [students, school.rooms],
  );

  const dayRecords = useMemo(
    () => records.filter((record) => record.day === selectedDay),
    [records, selectedDay],
  );

  const rows = useMemo(() => {
    const matchesFilters = (student: StudentPayload | null) =>
      (!selectedGrade || student?.grade === selectedGrade) &&
      (!selectedRoom || student?.room === selectedRoom);

    const rowsByKey = new Map<string, AttendanceDayRow>();
    for (const student of students) {
      if (!matchesFilters(student)) continue;
      rowsByKey.set(student.id, { key: student.id, student, checkin: null, checkout: null });
    }

    for (const record of dayRecords) {
      if (!matchesFilters(record.student)) continue;

      const key = studentKey(record.student);
      const existing = rowsByKey.get(key) ?? {
        key,
        student: record.student,
        checkin: null,
        checkout: null,
      };

      if (record.attendance === 'checkin') {
        existing.checkin = record;
      } else {
        existing.checkout = record;
      }

      rowsByKey.set(key, existing);
    }

    return Array.from(rowsByKey.values())
      .filter((row) => {
        if (showCheckedInOnly && !row.checkin) return false;
        if (showCheckedOutOnly && !row.checkout) return false;
        return true;
      })
      .sort((left, right) => {
        const leftName = left.student
          ? `${left.student.lastName} ${left.student.firstName}`
          : '';
        const rightName = right.student
          ? `${right.student.lastName} ${right.student.firstName}`
          : '';
        return leftName.localeCompare(rightName);
      });
  }, [dayRecords, showCheckedInOnly, showCheckedOutOnly, selectedGrade, selectedRoom, students]);

  const missingCount = rows.filter((row) => !row.checkin && !row.checkout).length;

  const showCheckInColumn = !showCheckedOutOnly || showCheckedInOnly;
  const showCheckOutColumn = !showCheckedInOnly || showCheckedOutOnly;

  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: school.timezone || 'UTC',
      }),
    [school.timezone],
  );

  const formatEvent = (record: AttendanceHistoryRecord | null) => {
    if (!record) return '—';
    return `${timeFormatter.format(new Date(record.at))} · ${record.by}`;
  };

  const filterSummary = useMemo(() => {
    const parts = [
      selectedGrade || 'All grades',
      selectedRoom || 'All rooms',
    ];
    if (showCheckedInOnly) parts.push('Checked in only');
    if (showCheckedOutOnly) parts.push('Checked out only');
    return parts.join(' · ');
  }, [selectedGrade, selectedRoom, showCheckedInOnly, showCheckedOutOnly]);

  const closeConfirm = () => {
    if (submitting) return;
    setPendingAction(null);
    setActionError(null);
  };

  const handleConfirm = async () => {
    if (!pendingAction || !user || !selectedDay) return;

    setSubmitting(true);
    setActionError(null);
    try {
      const response = await fetch('/api/attendance/staff', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          studentId: pendingAction.student.id,
          attendance: pendingAction.attendance,
          by: staffDisplayName(user),
          day: selectedDay,
        }),
      });
      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? `Submit failed (${response.status})`);
      }

      setPendingAction(null);
      await load({ quiet: true });
    } catch (submitError) {
      setActionError(submitError instanceof Error ? submitError.message : 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <style>{`
        @media print {
          body.printing-attendance-records {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          body.printing-attendance-records > *:not(#attendance-records-print-root) {
            display: none !important;
          }
          body.printing-attendance-records #attendance-records-print-root {
            display: block !important;
            position: static !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-sizing: border-box !important;
            background: white !important;
            color: #000 !important;
            overflow: visible !important;
          }
          body.printing-attendance-records #attendance-records-print-root .attendance-print-title {
            margin: 0 !important;
            font-size: 16pt !important;
            font-weight: 700 !important;
            line-height: 1.2 !important;
          }
          body.printing-attendance-records #attendance-records-print-root .attendance-print-subtitle {
            margin: 0.12in 0 0 !important;
            font-size: 11pt !important;
            font-weight: 600 !important;
          }
          body.printing-attendance-records #attendance-records-print-root .attendance-print-filters {
            margin: 0.06in 0 0 !important;
            font-size: 9pt !important;
            color: #333 !important;
          }
          body.printing-attendance-records #attendance-records-print-root .attendance-print-table {
            width: 100% !important;
            margin-top: 0.2in !important;
            border-collapse: collapse !important;
            font-size: 9pt !important;
          }
          body.printing-attendance-records #attendance-records-print-root .attendance-print-table th,
          body.printing-attendance-records #attendance-records-print-root .attendance-print-table td {
            border: 1px solid #ccc !important;
            padding: 0.08in 0.1in !important;
            text-align: left !important;
            vertical-align: top !important;
          }
          body.printing-attendance-records #attendance-records-print-root .attendance-print-table th {
            background: #f3f3f3 !important;
            font-size: 8pt !important;
            font-weight: 700 !important;
            text-transform: uppercase !important;
            letter-spacing: 0.02em !important;
          }
          body.printing-attendance-records #attendance-records-print-root .attendance-print-table thead {
            display: table-header-group !important;
          }
          body.printing-attendance-records #attendance-records-print-root .attendance-print-table tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
        }
      `}</style>

      {printing ? (
        <AttendanceRecordsPrintSheet
          schoolName={school.name}
          dayLabel={selectedDay ? formatDay(selectedDay) : ''}
          filterSummary={filterSummary}
          rows={rows}
          showCheckInColumn={showCheckInColumn}
          showCheckOutColumn={showCheckOutColumn}
          formatEvent={formatEvent}
        />
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold text-ink">Attendance records</h2>
          <p className="mt-1 font-sans text-sm text-ink/65">
            Every student for the selected day, including those with no check-in or check-out.
          </p>
        </div>
        <button
          type="button"
          disabled={status !== 'ready' || rows.length === 0 || printing}
          onClick={() => setPrinting(true)}
          className="shrink-0 rounded-full bg-white px-4 py-2.5 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-ink/5 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {printing ? 'Printing…' : 'Print'}
        </button>
      </div>

      <div className="grid gap-3 rounded-2xl bg-skysoft/20 p-4 sm:grid-cols-3">
        <label className="block">
          <span className="mb-1 block font-sans text-xs font-semibold uppercase tracking-wide text-ink/55">
            Day
          </span>
          <select
            value={selectedDay}
            onChange={(event) => setSelectedDay(event.target.value)}
            disabled={days.length === 0}
            className="w-full rounded-xl border-2 border-ink/10 bg-white px-3 py-2 font-sans text-sm text-ink focus:border-skysoft focus:ring-skysoft disabled:opacity-60"
          >
            {days.map((day) => (
              <option key={day} value={day}>
                {formatDay(day)}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block font-sans text-xs font-semibold uppercase tracking-wide text-ink/55">
            Grade
          </span>
          <select
            value={selectedGrade}
            onChange={(event) => setSelectedGrade(event.target.value)}
            className="w-full rounded-xl border-2 border-ink/10 bg-white px-3 py-2 font-sans text-sm text-ink focus:border-skysoft focus:ring-skysoft"
          >
            <option value="">All grades</option>
            {grades.map((grade) => (
              <option key={grade} value={grade}>
                {grade}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="mb-1 block font-sans text-xs font-semibold uppercase tracking-wide text-ink/55">
            Room
          </span>
          <select
            value={selectedRoom}
            onChange={(event) => setSelectedRoom(event.target.value)}
            className="w-full rounded-xl border-2 border-ink/10 bg-white px-3 py-2 font-sans text-sm text-ink focus:border-skysoft focus:ring-skysoft"
          >
            <option value="">All rooms</option>
            {rooms.map((room) => (
              <option key={room} value={room}>
                {room}
              </option>
            ))}
          </select>
        </label>

        <div className="flex flex-wrap items-end gap-3 sm:col-span-3">
          <button
            type="button"
            role="switch"
            aria-checked={showCheckedInOnly}
            onClick={() => {
              setShowCheckedInOnly((current) => !current);
              setShowCheckedOutOnly(false);
            }}
            className={`inline-flex items-center gap-2 rounded-full px-3 py-2 font-sans text-sm font-semibold ring-2 transition ${
              showCheckedInOnly
                ? 'bg-skysoft/70 text-ink ring-ink/10'
                : 'bg-white text-ink/70 ring-ink/10 hover:text-ink'
            }`}
          >
            <span
              aria-hidden
              className={`h-2.5 w-2.5 rounded-full ${
                showCheckedInOnly ? 'bg-ink' : 'bg-ink/25'
              }`}
            />
            Show checked in only
          </button>
          <button
            type="button"
            role="switch"
            aria-checked={showCheckedOutOnly}
            onClick={() => {
              setShowCheckedOutOnly((current) => !current);
              setShowCheckedInOnly(false);
            }}
            className={`inline-flex items-center gap-2 rounded-full px-3 py-2 font-sans text-sm font-semibold ring-2 transition ${
              showCheckedOutOnly
                ? 'bg-skysoft/70 text-ink ring-ink/10'
                : 'bg-white text-ink/70 ring-ink/10 hover:text-ink'
            }`}
          >
            <span
              aria-hidden
              className={`h-2.5 w-2.5 rounded-full ${
                showCheckedOutOnly ? 'bg-ink' : 'bg-ink/25'
              }`}
            />
            Show checked out only
          </button>
        </div>
      </div>

      {status === 'loading' ? (
        <p className="rounded-2xl border-2 border-dashed border-ink/10 p-6 text-center font-sans text-sm text-ink/60">
          Loading attendance records…
        </p>
      ) : null}

      {status === 'error' ? (
        <div className="rounded-2xl bg-doghouse/15 p-4">
          <p className="font-sans text-sm font-semibold text-ink" role="alert">
            {error}
          </p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-3 rounded-full bg-white px-4 py-2 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10"
          >
            Try again
          </button>
        </div>
      ) : null}

      {status === 'ready' && rows.length === 0 ? (
        <p className="rounded-2xl border-2 border-dashed border-ink/10 p-8 text-center font-sans text-sm text-ink/60">
          No students match these filters.
        </p>
      ) : null}

      {status === 'ready' && rows.length > 0 ? (
        <div className="overflow-x-auto rounded-2xl border-2 border-ink/10">
          <table className="min-w-full divide-y divide-ink/10 text-left">
            <thead className="bg-paper/70">
              <tr className="font-sans text-xs font-semibold uppercase tracking-wide text-ink/55">
                <th className="px-4 py-3">Student</th>
                <th className="px-4 py-3">Grade</th>
                <th className="px-4 py-3">Room</th>
                {showCheckInColumn ? <th className="px-4 py-3">Check-in</th> : null}
                {showCheckOutColumn ? <th className="px-4 py-3">Check-out</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/10 bg-white">
              {rows.map((row) => (
                <tr
                  key={row.key}
                  className={`font-sans text-sm text-ink ${
                    !row.checkin && !row.checkout ? 'bg-paper/40' : ''
                  }`}
                >
                  <td className="whitespace-nowrap px-4 py-3 font-semibold">
                    {row.student
                      ? `${row.student.firstName} ${row.student.lastName}`
                      : 'Student removed'}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">{row.student?.grade || '—'}</td>
                  <td className="whitespace-nowrap px-4 py-3">{row.student?.room || '—'}</td>
                  {showCheckInColumn ? (
                    <td className="whitespace-nowrap px-4 py-3">
                      {row.checkin ? (
                        formatEvent(row.checkin)
                      ) : row.student ? (
                        <button
                          type="button"
                          onClick={() => {
                            setActionError(null);
                            setPendingAction({ student: row.student!, attendance: 'checkin' });
                          }}
                          className="rounded-full bg-skysoft/70 px-3 py-1.5 text-xs font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-skysoft"
                        >
                          Check in
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  ) : null}
                  {showCheckOutColumn ? (
                    <td className="whitespace-nowrap px-4 py-3">
                      {row.checkout ? (
                        formatEvent(row.checkout)
                      ) : row.student ? (
                        <button
                          type="button"
                          onClick={() => {
                            setActionError(null);
                            setPendingAction({ student: row.student!, attendance: 'checkout' });
                          }}
                          className="rounded-full bg-amber-100 px-3 py-1.5 text-xs font-semibold text-ink ring-2 ring-ink/10 transition hover:bg-amber-200"
                        >
                          Check out
                        </button>
                      ) : (
                        '—'
                      )}
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {status === 'ready' && selectedDay ? (
        <p className="font-sans text-xs text-ink/50">
          {rows.length} student{rows.length === 1 ? '' : 's'} on {formatDay(selectedDay)} ·{' '}
          {missingCount} with no check-in or check-out
        </p>
      ) : null}

      <Transition appear show={pendingAction != null} as={Fragment}>
        <Dialog as="div" className="relative z-[60]" onClose={closeConfirm}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-ink/30" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md rounded-3xl border-[3px] border-ink/15 bg-white p-6 shadow-[4px_8px_0_rgba(44,44,44,0.1)]">
                  <Dialog.Title className="font-display text-xl font-semibold text-ink">
                    {pendingAction?.attendance === 'checkout'
                      ? 'Confirm check-out?'
                      : 'Confirm check-in?'}
                  </Dialog.Title>
                  <Dialog.Description className="mt-2 font-sans text-sm text-ink/70">
                    {pendingAction ? (
                      <>
                        Record a{' '}
                        {pendingAction.attendance === 'checkout' ? 'check-out' : 'check-in'} for{' '}
                        <span className="font-semibold text-ink">
                          {studentDisplayName(pendingAction.student)}
                        </span>{' '}
                        on {formatDay(selectedDay)}. No signature is required.
                      </>
                    ) : null}
                  </Dialog.Description>

                  {actionError ? (
                    <p
                      className="mt-4 rounded-xl bg-doghouse/15 px-3 py-2 font-sans text-sm font-semibold text-ink"
                      role="alert"
                    >
                      {actionError}
                    </p>
                  ) : null}

                  <div className="mt-6 flex flex-wrap justify-end gap-3">
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={closeConfirm}
                      className="rounded-full px-4 py-2 font-sans text-sm font-semibold text-ink/70 ring-2 ring-ink/10 hover:bg-ink/5 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={submitting || !user}
                      onClick={() => void handleConfirm()}
                      className="rounded-full bg-skysoft/70 px-5 py-2 font-sans text-sm font-semibold text-ink ring-2 ring-ink/10 hover:bg-skysoft disabled:opacity-50"
                    >
                      {submitting
                        ? pendingAction?.attendance === 'checkout'
                          ? 'Checking out…'
                          : 'Checking in…'
                        : pendingAction?.attendance === 'checkout'
                          ? 'Confirm check-out'
                          : 'Confirm check-in'}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default AttendanceHistoryPanel;
